import React, { useState, useEffect, useMemo, useRef } from 'react';
import './convenio.css';
import { bffExecuteInsuranceAction } from '../../functions/apiCalls/bffService';

// Componente de seleção de convênio/empresa/plano
// Recebe callbacks:
// - onConfirm: devolve o objeto selecionado
// - onFastOcr: dispara leitura automática da carteirinha
// - onGoToParticular: permite o usuário migrar para atendimento particular
export default function Convenio({ onConfirm, onFastOcr, onGoToParticular, isCompleted = false, requestId = '', defaultInsuranceId = '', forceManualFlow = false, examesSelecionados = null }) {
    const [convenio, setConvenio] = useState('');
    const [convenioResolvedId, setConvenioResolvedId] = useState('');
    const [empresa, setEmpresa] = useState('');
    const [plano, setPlano] = useState('');
    const [convenios, setConvenios] = useState([]);
    const [loadingConvenios, setLoadingConvenios] = useState(false);
    const [errorConvenios, setErrorConvenios] = useState('');
    const [empresas, setEmpresas] = useState([]);
    const [empresaToConvenioMap, setEmpresaToConvenioMap] = useState({});
    const [loadingEmpresas, setLoadingEmpresas] = useState(false);
    const [errorEmpresas, setErrorEmpresas] = useState('');
    const [planos, setPlanos] = useState([]);
    const [loadingPlanos, setLoadingPlanos] = useState(false);
    const [, setErrorPlanos] = useState('');
    const [showCardModal, setShowCardModal] = useState(false);
    const [cardModalVisible, setCardModalVisible] = useState(false);
    const [cardNumber, setCardNumber] = useState('');
    const [cardValidThru, setCardValidThru] = useState('');
    const [cardError, setCardError] = useState('');
    const [cardValidThruError, setCardValidThruError] = useState('');
    const [openMenu, setOpenMenu] = useState(false);
    const [query, setQuery] = useState('');
    const dropdownRef = useRef(null);
    const [openMenuEmp, setOpenMenuEmp] = useState(false);
    const [queryEmp, setQueryEmp] = useState('');
    const dropdownEmpRef = useRef(null);
    const [openMenuPlano, setOpenMenuPlano] = useState(false);
    const [queryPlano, setQueryPlano] = useState('');
    const dropdownPlanoRef = useRef(null);

    // Lista de convênios que disparam OCR direto (id -> nome)
    const fastOcrIds = useMemo(() => ({
        '4094': 'Bradesco',
        '6267': 'Bradesco operadora de planos',
        '3624': 'Sul América',
        '3466': 'Amil',
        '3583': 'Porto Seguro',
        '3632': 'Unimed Seguros',
        '3507': 'Central Nacional Unimed'
    }), []);

    // Montar array de IDs de exames a partir da estrutura examesSelecionados (usado em todas as chamadas)
    const examIds = useMemo(() => {
        if (!examesSelecionados) return null;

        // Caso padrão: objeto com campo procedimentos (vindo do OCR)
        if (Array.isArray(examesSelecionados.procedimentos)) {
            return examesSelecionados.procedimentos
                .map((p) => (p && p.id != null ? String(p.id) : null))
                .filter((id) => id);
        }

        // Caso alternativo: já vir como array (de objetos ou de ids)
        if (Array.isArray(examesSelecionados)) {
            return examesSelecionados
                .map((e) => {
                    if (e == null) return null;
                    if (typeof e === 'string' || typeof e === 'number') return String(e);
                    if (e.id != null) return String(e.id);
                    return null;
                })
                .filter((id) => id);
        }

        return null;
    }, [examesSelecionados]);

    // Carregar convênios da API
    useEffect(() => {
        const fetchConvenios = async () => {
            try {
                setLoadingConvenios(true);
                setErrorConvenios('');

                const url = process.env.REACT_APP_INSURANCE_API_URL;
                const apiKey = process.env.REACT_APP_INSURANCE_API_KEY;
                const brandId = process.env.REACT_APP_BRAND_ID || 'AM';
                const officeId = process.env.REACT_APP_OFFICE_ID || 'SP';

                // Em fluxo de "re-seleção" (forceManualFlow) após erro de OCR da carteirinha,
                // precisamos garantir que o convênio originalmente selecionado pelo usuário
                // seja carregado, mesmo que o filtro por exames não retorne nada.
                //
                // Por isso, quando forceManualFlow === true e temos um defaultInsuranceId,
                // enviamos esse ID explicitamente para a API e NÃO filtramos por exames.
                // No fluxo normal (primeira seleção), mantemos o comportamento antigo
                // buscando todos os convênios compatíveis com os exames.

                // Sempre que tivermos exames, devemos enviá-los como filtro para a API,
                // inclusive na re-seleção manual (forceManualFlow), para manter a
                // compatibilidade de cobertura entre exames e convênios.
                const shouldFilterByExams = !!examIds && examIds.length > 0;

                const contextBase = {
                    action: 'GetInsurancesHealthBookingAction',
                    brandId: brandId,
                    officeId: officeId,
                    requestId: String(requestId || ''),
                    isInsurance: true,
                    // Quando for re-seleção manual, focar no convênio previamente escolhido
                    ...(forceManualFlow && defaultInsuranceId && {
                        insuranceId: String(defaultInsuranceId)
                    }),
                    ...(shouldFilterByExams && { exams: examIds })
                };

                const body = {
                    context: contextBase,
                    code_execute: ''
                };

                let data;
                if (process.env.REACT_APP_BFF_API_URL || !apiKey) {
                    data = await bffExecuteInsuranceAction(contextBase, '');
                } else {
                    const resp = await fetch(url, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'x-api-key': apiKey
                        },
                        body: JSON.stringify(body)
                    });

                    if (!resp.ok) {
                        throw new Error(`HTTP ${resp.status}`);
                    }
                    data = await resp.json();
                }

                const rawInsurances = Array.isArray(data?.context?.insurances)
                    ? (data?.context?.insurances || [])
                    : (data?.context?.insurances?.data || data?.context?.data || []);

                // mapear para { id, ids, nome } aceitando novo e antigo formato
                const mapped = rawInsurances
                    .map((item) => {
                        const idVal = item?.insuranceId ?? item?.ID_CONV_CD_CONVENIO ?? item?.id ?? item?.ID;
                        const nameVal = item?.insuranceName ?? item?.OPER_NO_OPERADORA ?? item?.name ?? item?.NOME ?? '';
                        const idStr = idVal !== undefined && idVal !== null ? String(idVal) : '';
                        const nameStr = nameVal !== undefined && nameVal !== null ? String(nameVal) : '';
                        return {
                            id: idStr,
                            ids: idStr ? [idStr] : [],
                            nome: nameStr,
                            isFastOcr: idStr ? (fastOcrIds[idStr] !== undefined) : false
                        };
                    })
                    .filter((it) => it.id && it.nome && it.nome !== 'undefined');

                const sorted = mapped.sort((a, b) => {
                    const byName = a.nome.localeCompare(b.nome);
                    return byName !== 0 ? byName : String(a.id).localeCompare(String(b.id));
                });

                if (sorted.length === 0) {
                    setErrorConvenios('Nenhum convênio disponível no momento.');
                }
                setConvenios(sorted);
            } catch (err) {
                setErrorConvenios('Não foi possível carregar os convênios.');
                setConvenios([]);
            } finally {
                setLoadingConvenios(false);
            }
        };

        fetchConvenios();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestId]);

    // Log do convênio selecionado para depuração
    useEffect(() => {
        if (convenio) {
            const selected = convenios.find(c => c.id === convenio);
            if (selected) {
                console.log('Convênio selecionado:', { id: selected.id, nome: selected.nome, isFastOcr: !!selected.isFastOcr });
            } else {
                console.log('Convênio selecionado (ID):', convenio);
            }
        }
    }, [convenio, convenios]);

    // Buscar empresas quando um convênio for escolhido
    useEffect(() => {
        const fetchEmpresas = async () => {
            try {
                setLoadingEmpresas(true);
                setErrorEmpresas('');
                setEmpresas([]);
                setEmpresaToConvenioMap({});

                const url = process.env.REACT_APP_INSURANCE_API_URL;
                const apiKey = process.env.REACT_APP_INSURANCE_API_KEY;
                const brandId = process.env.REACT_APP_BRAND_ID || 'AM';
                const officeId = process.env.REACT_APP_OFFICE_ID || 'SP';

                // Captura IDs relacionados caso o item selecionado tenha sido aglutinado
                const selectedItem = convenios.find(c => c.id === convenio);
                const insuranceIds = selectedItem?.ids && Array.isArray(selectedItem.ids) && selectedItem.ids.length > 0
                    ? selectedItem.ids
                    : [String(convenio)];

                // Busca empresas para cada ID de convênio em paralelo
                const requests = insuranceIds.map(async (insuranceId) => {
                    const context = {
                        action: 'GetInsurancesHealthBookingAction',
                        brandId: brandId,
                        officeId: officeId,
                        requestId: String(requestId || ''),
                        isCompany: true,
                        insuranceId: String(insuranceId),
                        ...(examIds && examIds.length > 0 && { exams: examIds })
                    };

                    if (process.env.REACT_APP_BFF_API_URL || !apiKey) {
                        try {
                            const json = await bffExecuteInsuranceAction(context, '');
                            return { ok: true, status: 200, json, insuranceId };
                        } catch (e) {
                            return { ok: false, status: 500, json: null, insuranceId };
                        }
                    }

                    const body = { context, code_execute: '' };
                    return fetch(url, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'x-api-key': apiKey
                        },
                        body: JSON.stringify(body)
                    }).then(async (r) => ({ ok: r.ok, status: r.status, json: r.ok ? await r.json() : null, insuranceId }));
                });

                const results = await Promise.allSettled(requests);

                // Helper para extrair campos de maneira tolerante
                const pickFirst = (obj, fields) => {
                    for (const f of fields) {
                        const val = obj?.[f];
                        if (val !== undefined && val !== null && String(val).trim() !== '') return val;
                    }
                    return '';
                };

                const combined = [];
                const empresaMap = {}; // empresaId -> convenioId de origem

                for (const res of results) {
                    if (res.status !== 'fulfilled' || !res.value.ok) continue;
                    const payload = res.value.json;
                    const rawCompanies = Array.isArray(payload?.context?.companies)
                        ? (payload?.context?.companies || [])
                        : (payload?.context?.companies?.data
                            || payload?.context?.insurances?.data
                            || payload?.context?.Empresas
                            || payload?.context?.data
                            || []);

                    const mapped = rawCompanies
                        .map((item) => {
                            const id = String(pickFirst(item, [
                                'companyId',
                                'ID_EMCV_CD_EMPRCONVENIO',
                                'ID_EMP_CD_EMPRESA', 'empresaId', 'ID_EMPRESA', 'id', 'ID'
                            ]));
                            const nome = String(pickFirst(item, [
                                'companyName',
                                'EMPR_DS_EMPRCONVENIO',
                                'EMP_NO_EMPRESA', 'EMP_NM_EMPRESA', 'empresa', 'NOME', 'name', 'nome'
                            ]));
                            if (!id || !nome || nome === 'undefined') return null;
                            // Registra origem dessa empresa
                            if (!empresaMap[id]) empresaMap[id] = new Set();
                            empresaMap[id].add(String(res.value.insuranceId));
                            return { id, nome };
                        })
                        .filter(Boolean);

                    combined.push(...mapped);
                }

                // Deduplicar por nome + id
                const unique = Array.from(new Map(combined.map(m => [m.id, m])).values());
                // Persistir o mapeamento empresa -> lista de convênios de origem
                const finalEmpresaMap = Object.fromEntries(
                    Object.entries(empresaMap).map(([k, v]) => [k, Array.from(v)])
                );
                setEmpresaToConvenioMap(finalEmpresaMap);
                setEmpresas(unique);

            } catch (err) {
                setErrorEmpresas('Não foi possível carregar as empresas.');
                setEmpresas([]);
            } finally {
                setLoadingEmpresas(false);
            }
        };

        // Resetar seleção quando o convênio muda
        setEmpresa('');
        setEmpresas([]);
        setErrorEmpresas('');
        if (convenio) {
            fetchEmpresas();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [convenio]);

    // Buscar planos quando uma empresa for escolhida
    useEffect(() => {
        const fetchPlanos = async () => {
            try {
                setLoadingPlanos(true);
                setErrorPlanos('');
                setPlanos([]);

                const url = process.env.REACT_APP_INSURANCE_API_URL;
                const apiKey = process.env.REACT_APP_INSURANCE_API_KEY;
                const brandId = process.env.REACT_APP_BRAND_ID || 'AM';
                const officeId = process.env.REACT_APP_OFFICE_ID || 'SP';

                // Descobre o convênio correto para a empresa selecionada, se houver mapeamento
                let insuranceIdForPlan = String(convenio);
                if (empresa && empresaToConvenioMap && empresaToConvenioMap[empresa] && empresaToConvenioMap[empresa].length > 0) {
                    // Por enquanto, se houver múltiplos, escolha o primeiro (poderia haver heurística extra)
                    insuranceIdForPlan = String(empresaToConvenioMap[empresa][0]);
                }
                setConvenioResolvedId(insuranceIdForPlan);

                const context = {
                    action: 'GetInsurancesHealthBookingAction',
                    brandId: brandId,
                    officeId: officeId,
                    requestId: String(requestId || ''),
                    isPlan: true,
                    companyId: String(empresa),
                    insuranceId: insuranceIdForPlan,
                    ...(examIds && examIds.length > 0 && { exams: examIds })
                };

                let data;
                if (process.env.REACT_APP_BFF_API_URL || !apiKey) {
                    data = await bffExecuteInsuranceAction(context, '');
                } else {
                    const body = { context, code_execute: '' };
                    const resp = await fetch(url, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'x-api-key': apiKey
                        },
                        body: JSON.stringify(body)
                    });

                    if (!resp.ok) {
                        throw new Error(`HTTP ${resp.status}`);
                    }
                    data = await resp.json();
                }

                const rawPlans = Array.isArray(data?.context?.plans)
                    ? (data?.context?.plans || [])
                    : (data?.context?.plans?.data || data?.context?.data || []);

                const mapped = rawPlans
                    .map((item) => ({
                        id: String(item?.planId ?? item?.ID_PLSA_CD_PLANOSAUDE ?? item?.id ?? item?.ID),
                        nome: String(item?.planName ?? item?.PLSA_DS_PLANOSAUDE ?? item?.nome ?? item?.name ?? '')
                    }))
                    .filter((p) => p.id && p.nome && p.nome !== 'undefined');

                const unique = Array.from(new Map(mapped.map(m => [m.nome, m])).values());
                setPlanos(unique);
            } catch (err) {
                setErrorPlanos('Não foi possível carregar os planos.');
                setPlanos([]);
            } finally {
                setLoadingPlanos(false);
            }
        };

        // Resetar seleção quando a empresa muda
        setPlano('');
        setPlanos([]);
        setErrorPlanos('');
        if (empresa) {
            fetchPlanos();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [empresa]);

    // Preselecionar convênio quando fornecido via props (após carregar lista)
    useEffect(() => {
        if (defaultInsuranceId && convenios.length > 0) {
            const exists = convenios.some(c => String(c.id) === String(defaultInsuranceId));
            if (exists && String(convenio) !== String(defaultInsuranceId)) {
                setConvenio(String(defaultInsuranceId));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultInsuranceId, convenios]);

    const selectedConvenio = useMemo(() => convenios.find(c => c.id === convenio), [convenios, convenio]);
    const ocrCarteirinhaEnabled = process.env.REACT_APP_OCR_CARTEIRINHA_ENABLED !== 'false';
    const isFastOcrSelected = (!forceManualFlow) && ocrCarteirinhaEnabled && (selectedConvenio?.isFastOcr === true);
    const canConfirm = isFastOcrSelected ? Boolean(convenio) : Boolean(convenio && empresa && plano);

    // Helper para validar data DD/MM/AAAA e garantir hoje/futura
    const isDateTodayOrFuture = (dt) => {
        const v = (dt || '').trim();
        const re = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
        if (!re.test(v)) return false;
        const [ddStr, mmStr, yyyyStr] = v.split('/');
        const dd = parseInt(ddStr, 10);
        const mm = parseInt(mmStr, 10) - 1;
        const yyyy = parseInt(yyyyStr, 10);
        const inputDate = new Date(yyyy, mm, dd);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return inputDate >= today;
    };

    const handleConfirm = () => {
        if (!canConfirm) return;
        if (isFastOcrSelected && typeof onFastOcr === 'function') {
            onFastOcr(convenio, selectedConvenio?.nome || '');
            return;
        }
        setShowCardModal(true);
    };

    const handleSubmitCard = () => {
        const numeric = cardNumber.replace(/\D+/g, '');
        if (numeric.length === 0) {
            setCardError('Digite o número da carteirinha');
            return;
        }
        // validar data no formato DD/MM/AAAA apenas se preenchida (opcional)
        const dt = (cardValidThru || '').trim();
        if (dt.length > 0) {
            const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
            if (!dateRegex.test(dt)) {
                setCardValidThruError('Informe a data no formato DD/MM/AAAA ou deixe em branco');
                return;
            }
            // Validar se a data é hoje ou futura
            const [ddStr, mmStr, yyyyStr] = dt.split('/');
            const dd = parseInt(ddStr, 10);
            const mm = parseInt(mmStr, 10) - 1; // mês 0-index
            const yyyy = parseInt(yyyyStr, 10);
            const inputDate = new Date(yyyy, mm, dd);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (inputDate < today) {
                setCardValidThruError('A data deve ser hoje ou futura');
                return;
            }
        }
        // Determina o convênio real a ser enviado ao backend
        const selectedItem = convenios.find(c => c.id === convenio);
        const resolvedConvenioId = String(convenioResolvedId || (selectedItem?.ids?.[0] || convenio));

        const payload = {
            convenio: (convenios.find(c => c.id === convenio)?.nome || convenio),
            empresa: empresas.find(e => e.id === empresa)?.nome || empresa,
            plano: planos.find(p => p.id === plano)?.nome || plano,
            ids: { convenio: resolvedConvenioId, empresa, plano },
            carteirinhaNumero: numeric,
            carteirinhaValidThru: dt,
            isManual: true
        };
        // fechar com animação estilo bottom sheet
        setCardModalVisible(false);
        setTimeout(() => {
            setShowCardModal(false);
        }, 250);
        setCardError('');
        setCardValidThruError('');
        if (onConfirm) onConfirm(payload);
    };

    // controlar animação e scroll do body para o modal estilo BuscaExames
    useEffect(() => {
        if (showCardModal) {
            setTimeout(() => setCardModalVisible(true), 10);
            document.body.classList.add('modal-open');
            document.body.style.overflow = 'hidden';
        } else {
            setCardModalVisible(false);
            document.body.classList.remove('modal-open');
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.classList.remove('modal-open');
            document.body.style.overflow = 'unset';
        };
    }, [showCardModal]);

    // Fechar menu ao clicar fora
    useEffect(() => {
        const onClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setOpenMenu(false);
            }
            if (dropdownEmpRef.current && !dropdownEmpRef.current.contains(e.target)) {
                setOpenMenuEmp(false);
            }
            if (dropdownPlanoRef.current && !dropdownPlanoRef.current.contains(e.target)) {
                setOpenMenuPlano(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const filteredConvenios = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return convenios;
        return convenios.filter(c => c.nome.toLowerCase().includes(q));
    }, [query, convenios]);

    const filteredEmpresas = useMemo(() => {
        const q = queryEmp.trim().toLowerCase();
        if (!q) return empresas;
        return empresas.filter(e => e.nome.toLowerCase().includes(q));
    }, [queryEmp, empresas]);

    const filteredPlanos = useMemo(() => {
        const q = queryPlano.trim().toLowerCase();
        if (!q) return planos;
        return planos.filter(p => p.nome.toLowerCase().includes(q));
    }, [queryPlano, planos]);

    if (isCompleted) {
        return (
            <div className="convenio-container">
                <div className="convenio-card completed-simple">
                    <div className="convenio-content">
                        <p>Obrigado! Seus dados de convênio foram registrados.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="convenio-container">
            <div className="convenio-wrapper">
                {loadingConvenios && (
                    <div className="convenio-overlay" aria-hidden="true">
                        <div className="convenio-spinner"></div>
                    </div>
                )}
                <div className={`convenio-card ${loadingConvenios ? 'loading' : ''}`} aria-busy={loadingConvenios} style={{ position: 'relative' }}>
                <h3 className="convenio-title">Selecione seu convênio</h3>

                <div className="convenio-grid">
                    <div className="input-group">
                        <label>Convênio</label>

                        {/* Dropdown customizado */}
                        <div className={`dropdown ${openMenu ? 'open' : ''}`} ref={dropdownRef}>
                            <button
                                type="button"
                                className="dropdown-control"
                                onClick={() => !loadingConvenios && setOpenMenu(!openMenu)}
                                disabled={loadingConvenios}
                                aria-expanded={openMenu}
                            >
                                {loadingConvenios ? 'Carregando...' : (convenio ? (convenios.find(c => c.id === convenio)?.nome || 'Escolha o convênio') : 'Escolha o convênio')}
                                <span className="caret" />
                            </button>

                            {openMenu && !loadingConvenios && (
                                <div className="dropdown-menu">
                                    <div className="dropdown-search">
                                        <input
                                            type="text"
                                            placeholder="Pesquisar convênio"
                                            value={query}
                                            onChange={(e) => setQuery(e.target.value)}
                                            autoFocus
                                        />
                                    </div>
                                    <div className="dropdown-list">
                                        {filteredConvenios.length === 0 && (
                                            <div className="dropdown-empty">Nenhum resultado</div>
                                        )}
                                        {filteredConvenios.map((c) => (
                                            <div
                                                key={c.id}
                                                className={`dropdown-item ${convenio === c.id ? 'selected' : ''}`}
                                                onClick={() => { setConvenio(c.id); setOpenMenu(false); }}
                                                role="button"
                                                tabIndex={0}
                                            >
                                                {c.nome}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        {errorConvenios && !loadingConvenios && (
                            <small className="convenio-error">{errorConvenios}</small>
                        )}
                    </div>

                    {convenio && !isFastOcrSelected && (
                        loadingEmpresas ? (
                            <div className="input-group">
                                <label>Empresa</label>
                                <div className="field-wrapper">
                                    <button type="button" className="dropdown-control loading" disabled>Carregando...</button>
                                    <div className="convenio-overlay" aria-hidden="true">
                                        <div className="convenio-spinner"></div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            empresas.length > 0 && (
                                <div className="input-group">
                                    <label>Empresa</label>
                                    <div className={`dropdown ${openMenuEmp ? 'open' : ''}`} ref={dropdownEmpRef}>
                                        <button
                                            type="button"
                                            className="dropdown-control"
                                            onClick={() => convenio && !loadingEmpresas && setOpenMenuEmp(!openMenuEmp)}
                                            disabled={!convenio || loadingEmpresas}
                                            aria-expanded={openMenuEmp}
                                        >
                                            {!convenio ? 'Selecione um convênio primeiro' : (loadingEmpresas ? 'Carregando...' : (empresa ? (empresas.find(e => e.id === empresa)?.nome || 'Escolha a empresa') : 'Escolha a empresa'))}
                                            <span className="caret" />
                                        </button>

                                        {openMenuEmp && convenio && !loadingEmpresas && (
                                            <div className="dropdown-menu">
                                                <div className="dropdown-search">
                                                    <input
                                                        type="text"
                                                        placeholder="Pesquisar empresa"
                                                        value={queryEmp}
                                                        onChange={(e) => setQueryEmp(e.target.value)}
                                                        autoFocus
                                                    />
                                                </div>
                                                <div className="dropdown-list">
                                                    {filteredEmpresas.length === 0 && (
                                                        <div className="dropdown-empty">Nenhum resultado</div>
                                                    )}
                                                    {filteredEmpresas.map((e) => (
                                                        <div
                                                            key={e.id}
                                                            className={`dropdown-item ${empresa === e.id ? 'selected' : ''}`}
                                                            onClick={() => { setEmpresa(e.id); setOpenMenuEmp(false); }}
                                                            role="button"
                                                            tabIndex={0}
                                                        >
                                                            {e.nome}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {errorEmpresas && !loadingEmpresas && convenio && (
                                        <small className="convenio-error">{errorEmpresas}</small>
                                    )}
                                </div>
                            )
                        )
                    )}

                    {empresa && !isFastOcrSelected && (
                        loadingPlanos ? (
                            <div className="input-group">
                                <label>Plano</label>
                                <div className="field-wrapper">
                                    <button type="button" className="dropdown-control loading" disabled>Carregando...</button>
                                    <div className="convenio-overlay" aria-hidden="true">
                                        <div className="convenio-spinner"></div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            planos.length > 0 && (
                                <div className="input-group">
                                    <label>Plano</label>
                                    <div className={`dropdown ${openMenuPlano ? 'open' : ''}`} ref={dropdownPlanoRef}>
                                        <button
                                            type="button"
                                            className="dropdown-control"
                                            onClick={() => setOpenMenuPlano(!openMenuPlano)}
                                            aria-expanded={openMenuPlano}
                                        >
                                            {plano ? (planos.find(p => p.id === plano)?.nome || 'Escolha o plano') : 'Escolha o plano'}
                                            <span className="caret" />
                                        </button>

                                        {openMenuPlano && (
                                            <div className="dropdown-menu">
                                                <div className="dropdown-search">
                                                    <input
                                                        type="text"
                                                        placeholder="Pesquisar plano"
                                                        value={queryPlano}
                                                        onChange={(e) => setQueryPlano(e.target.value)}
                                                        autoFocus
                                                    />
                                                </div>
                                                <div className="dropdown-list">
                                                    {!loadingPlanos && filteredPlanos.map((p) => (
                                                        <div
                                                            key={p.id}
                                                            className={`dropdown-item ${plano === p.id ? 'selected' : ''}`}
                                                            onClick={() => { setPlano(p.id); setOpenMenuPlano(false); }}
                                                            role="button"
                                                            tabIndex={0}
                                                        >
                                                            {p.nome}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )
                        )
                    )}
                </div>

                <button
                    className="convenio-confirm-button"
                    onClick={handleConfirm}
                    disabled={!canConfirm}
                >
                    Confirmar
                </button>

                {typeof onGoToParticular === 'function' && (
                    <button
                        type="button"
                        className="convenio-particular-link"
                        onClick={onGoToParticular}
                    >
                        <strong>Não achou seu convênio?</strong>{' '}
                        <strong>Aproveite 15% de desconto no atendimento particular</strong>, válido para a maioria dos procedimentos.
                    </button>
                )}
                </div>
            </div>

            {showCardModal && (
                <>
                    <div 
                        className={`modal-overlay ${cardModalVisible ? 'visible' : ''}`} 
                        onClick={() => { setCardModalVisible(false); setTimeout(() => setShowCardModal(false), 250); }}
                    ></div>
                    <div className={`modal-content-csat ${cardModalVisible ? 'visible' : ''}`} role="dialog" aria-modal="true">
                        <div className="modal-header">
                            <h3>Informe o número da carteirinha</h3>
                            <button 
                                className="close-button" 
                                onClick={() => { setCardModalVisible(false); setTimeout(() => setShowCardModal(false), 250); }}
                                aria-label="Fechar modal"
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="search-input-container">
                                <input
                                    type="tel"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    className="search-input"
                                    placeholder="Somente números"
                                    value={cardNumber}
                                    onChange={(e) => { setCardNumber(e.target.value.replace(/\D+/g, '')); setCardError(''); }}
                                    autoFocus
                                    aria-label="Número da carteirinha"
                                    autoComplete="one-time-code"
                                />
                            </div>
                            {cardError && <div className="search-error">{cardError}</div>}

                            <div className="search-input-container">
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    className="search-input"
                                    placeholder="Data de validade (se tiver): DD/MM/AAAA"
                                    value={cardValidThru}
                                    onChange={(e) => {
                                        const raw = e.target.value.replace(/\D/g, '').slice(0, 8);
                                        let formatted = raw;
                                        if (raw.length >= 5) {
                                            formatted = raw.replace(/(\d{2})(\d{2})(\d{0,4})/, '$1/$2/$3');
                                        } else if (raw.length >= 3) {
                                            formatted = raw.replace(/(\d{2})(\d{0,2})/, '$1/$2');
                                        }
                                        setCardValidThru(formatted);
                                        // validação imediata
                                        if (formatted.length === 10) {
                                            const re = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
                                            if (!re.test(formatted)) {
                                                setCardValidThruError('Informe a data no formato DD/MM/AAAA');
                                            } else if (!isDateTodayOrFuture(formatted)) {
                                                setCardValidThruError('A data deve ser hoje ou futura');
                                            } else {
                                                setCardValidThruError('');
                                            }
                                        } else if (formatted.length === 0) {
                                            setCardValidThruError('');
                                        } else {
                                            setCardValidThruError('');
                                        }
                                    }}
                                    aria-label="Data de validade (se tiver): DD/MM/AAAA"
                                    autoComplete="off"
                                />
                            </div>
                            {cardValidThruError && <div className="search-error">{cardValidThruError}</div>}
                        </div>
                        <div className="modal-footer">
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <button
                                    type="button"
                                    className="btn-primary"
                                    onClick={handleSubmitCard}
                                    disabled={
                                        cardNumber.replace(/\D+/g, '').length === 0 ||
                                        (cardValidThru.trim().length > 0 && (!isDateTodayOrFuture(cardValidThru) || !!cardValidThruError))
                                    }
                                >
                                    Enviar
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}


